/**
 * pdfSave — capture-side "save everything as PDF" pipeline (brief §2.3 / slot-1
 * G6/T8).
 *
 * Bill's rule: whatever the FSR captures (single photo, multi-page scan, or a
 * PDF that was dropped directly) is normalized to **one PDF** when saved, so
 * every attached document downstream is a uniform `application/pdf`. Multi-page
 * captures preserve every page in the same PDF.
 *
 * Uses `pdf-lib` bundled via npm — NO CDN (CLAUDE.md). pdf-lib embeds JPEG +
 * PNG natively and copies pages out of an existing PDF. Inputs that arrive as
 * TIFF / HEIC / HEIF have already been transcoded to JPEG by
 * `utils/imageConvert.preflightImageFile` before they reach the capture
 * handler, so this layer only needs to handle jpeg / png / pdf natively; any
 * other `image/*` MIME is coerced to JPEG via a canvas re-encode (covers
 * webp / bmp / gif edge cases).
 *
 * TR coordination (Contract / slot-1 T8): Azure DI extraction runs against the
 * ORIGINAL captured bytes (image or PDF) BEFORE this conversion — DI accepts
 * the native image and gives better OCR than a re-rendered PDF page. The PDF
 * produced here is the STORED artifact, not the extraction input. Callers
 * extract first, then call `mergeCapturesToPdf` to produce the bytes they
 * persist as the AttachedDoc.
 */

import { PDFDocument, type PDFImage } from 'pdf-lib';

/** US Letter, points (1/72"). Each captured image is fit onto a Letter page. */
const LETTER_W = 612;
const LETTER_H = 792;

/** One captured page/file feeding the merge. */
export interface CapturedPage {
  /** Raw base64 (no `data:` prefix). */
  base64: string;
  /** MIME of the bytes — 'application/pdf' | 'image/jpeg' | 'image/png' | other image/*. */
  mimeType: string;
}

export interface MergedPdfResult {
  /** Base64 of the produced PDF (no `data:` prefix). */
  base64: string;
  mimeType: 'application/pdf';
  /** Total pages in the produced PDF. */
  pageCount: number;
}

/** Decode a base64 string to bytes. Tolerates a `data:` prefix if present. */
function base64ToBytes(base64: string): Uint8Array {
  const comma = base64.indexOf(',');
  const clean = base64.startsWith('data:') && comma >= 0 ? base64.slice(comma + 1) : base64;
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Encode bytes to base64 without a `data:` prefix. */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000; // avoid call-stack overflow on large buffers
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Re-encode an arbitrary `image/*` base64 to JPEG via canvas. Used for image
 * MIMEs pdf-lib can't embed directly (webp/bmp/gif). HEIC/HEIF/TIFF never reach
 * here — preflight transcodes those to JPEG up-front. Returns JPEG base64.
 */
async function coerceImageToJpegBase64(base64: string, mimeType: string): Promise<string> {
  // `as BlobPart` — TS 5.6's lib types Uint8Array as Uint8Array<ArrayBufferLike>
  // which doesn't structurally match BlobPart's ArrayBufferView<ArrayBuffer>;
  // the bytes are a plain ArrayBuffer-backed view at runtime.
  const blob = new Blob([base64ToBytes(base64) as BlobPart], { type: mimeType || 'image/png' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error(`Could not decode ${mimeType || 'image'} for PDF conversion`));
      el.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable for PDF conversion');
    ctx.drawImage(img, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    return dataUrl.slice(dataUrl.indexOf(',') + 1);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Add one embedded image as a Letter page, fit-to-page preserving aspect. */
function addImagePage(out: PDFDocument, img: PDFImage): void {
  const scale = Math.min(LETTER_W / img.width, LETTER_H / img.height, 1);
  const w = img.width * scale;
  const h = img.height * scale;
  const page = out.addPage([LETTER_W, LETTER_H]);
  page.drawImage(img, {
    x: (LETTER_W - w) / 2,
    y: (LETTER_H - h) / 2,
    width: w,
    height: h,
  });
}

/**
 * Merge one or more captured pages/files into a single PDF.
 *
 *   - `application/pdf`  → all pages copied in order (multi-page preserved)
 *   - `image/jpeg`       → embedded as a Letter page (fit-to-page)
 *   - `image/png`        → embedded as a Letter page (fit-to-page)
 *   - other `image/*`    → canvas-coerced to JPEG, then embedded
 *
 * Returns the produced PDF as base64 + total page count. Throws a
 * user-displayable error if a page can't be embedded.
 */
export async function mergeCapturesToPdf(pages: ReadonlyArray<CapturedPage>): Promise<MergedPdfResult> {
  if (pages.length === 0) throw new Error('No captured pages to convert to PDF.');
  const out = await PDFDocument.create();

  for (const p of pages) {
    const mime = (p.mimeType || '').toLowerCase();
    if (mime === 'application/pdf') {
      const src = await PDFDocument.load(base64ToBytes(p.base64), { ignoreEncryption: true });
      const copied = await out.copyPages(src, src.getPageIndices());
      copied.forEach((pg) => out.addPage(pg));
      continue;
    }
    if (mime === 'image/jpeg' || mime === 'image/jpg') {
      addImagePage(out, await out.embedJpg(base64ToBytes(p.base64)));
      continue;
    }
    if (mime === 'image/png') {
      addImagePage(out, await out.embedPng(base64ToBytes(p.base64)));
      continue;
    }
    // Any other image/* — coerce to JPEG then embed. Non-image MIMEs are a
    // programming error upstream (capture only accepts the documented set).
    if (mime.startsWith('image/')) {
      const jpegB64 = await coerceImageToJpegBase64(p.base64, mime);
      addImagePage(out, await out.embedJpg(base64ToBytes(jpegB64)));
      continue;
    }
    throw new Error(`Unsupported capture type for PDF conversion: ${p.mimeType}`);
  }

  const bytes = await out.save();
  return { base64: bytesToBase64(bytes), mimeType: 'application/pdf', pageCount: out.getPageCount() };
}

/** Convenience single-file wrapper. */
export function convertCaptureToPdf(base64: string, mimeType: string): Promise<MergedPdfResult> {
  return mergeCapturesToPdf([{ base64, mimeType }]);
}
